import {
  createSpecialistLocationGatewayOwnerKey,
  encodeSpecialistLocationGatewayTuple,
} from "../canonicalKey";
import {
  isSpecialistLocationGatewayFingerprint,
  isSpecialistLocationGatewayIdentifier,
} from "../contract";

export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_SCHEMA_VERSION =
  1 as const;
export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_KIND =
  "specialist-location-gateway-configured-services-cart" as const;
export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_TTL_MS =
  24 * 60 * 60 * 1_000;
export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_LINES = 16;

const MAX_CLOCK_SKEW_MS = 5 * 60 * 1_000;
const MAX_QUANTITY = 99;
const MAX_ADD_ON_ROWS = 64;
const MAX_BUNDLE_ITEM_ROWS = 64;
const MAX_CANONICAL_FINGERPRINT_LENGTH = 360_000;
const CART_FINGERPRINT_PREFIX =
  "specialist-location-gateway-services-cart-fingerprint-v1:";
const MAPPING_FINGERPRINT_PREFIX =
  "specialist-location-gateway-services-cart-mapping-fingerprint-v1:";

export interface SpecialistLocationGatewayServicesPublicationReferenceV1 {
  providerId: string;
  providerRevision: string;
  publicationId: string;
  publicationRevision: string;
  publicationFingerprint: string;
  publishedAtEpochMs: number;
  expiresAtEpochMs: number;
}

export interface SpecialistLocationGatewayServicesCatalogReferenceV1 {
  locationId: string;
  catalogOwnerUserLocationId: string;
  providerId: string;
  providerRevision: string;
  publicationId: string;
  publicationRevision: string;
  catalogVersion: string;
  catalogFingerprint: string;
}

export interface SpecialistLocationGatewayServicesSourceAssignmentAV1 {
  personId: string;
  locationId: string;
  userLocationId: string;
  canonicalReturnHref: string;
  assignmentProfileRevision: string;
  catalog: SpecialistLocationGatewayServicesCatalogReferenceV1;
}

export interface SpecialistLocationGatewayServicesSelectedAssignmentBV1 {
  personId: string;
  locationId: string;
  userLocationId: string;
  assignmentProfileRevision: string;
  gatewayCapabilityRevision: string;
  organizerCatalog: SpecialistLocationGatewayServicesCatalogReferenceV1;
  locationCatalog: SpecialistLocationGatewayServicesCatalogReferenceV1;
}

export type SpecialistLocationGatewayServicesCartScopeV1 =
  | { kind: "unscoped" }
  | {
      kind: "scoped";
      intentKind: "service" | "service-bundle";
      globalRef: string;
      sourceServiceId: string;
      sourceOfferingId: string;
      seedLineId: string;
    };

export interface SpecialistLocationGatewayServicesCartAddOnSelectionV1 {
  groupId: string;
  addOnId: string;
  quantity: number;
}

export interface SpecialistLocationGatewayServicesCartBundleItemSelectionV1 {
  bundleItemId: string;
  serviceId: string;
  offeringId: string;
  variantId: string | null;
  addOns: readonly SpecialistLocationGatewayServicesCartAddOnSelectionV1[];
}

/**
 * A configuration is either complete as one exact value or absent. There is
 * deliberately no partially trusted variant/add-on/bundle representation.
 */
export interface SpecialistLocationGatewayServicesCartConfigurationV1 {
  variantId: string | null;
  addOns: readonly SpecialistLocationGatewayServicesCartAddOnSelectionV1[];
  bundleItems: readonly SpecialistLocationGatewayServicesCartBundleItemSelectionV1[];
}

export interface SpecialistLocationGatewayServicesCartVariantMappingV1 {
  sourceVariantId: string;
  destinationVariantId: string;
}

export interface SpecialistLocationGatewayServicesCartAddOnMappingV1 {
  /**
   * Canonical provider order, not a set index. Source selection, destination
   * selection, and mapping must occupy this same position. A reorder is a new
   * configuration publication and therefore produces a new fingerprint.
   */
  order: number;
  sourceGroupId: string;
  sourceAddOnId: string;
  destinationGroupId: string;
  destinationAddOnId: string;
  quantity: number;
}

export interface SpecialistLocationGatewayServicesCartBundleItemMappingV1 {
  /** Same positional/provider-order contract as parent add-on mappings. */
  order: number;
  sourceBundleItemId: string;
  sourceServiceId: string;
  sourceOfferingId: string;
  destinationBundleItemId: string;
  destinationServiceId: string;
  destinationOfferingId: string;
  variantMapping: SpecialistLocationGatewayServicesCartVariantMappingV1 | null;
  addOnMappings: readonly SpecialistLocationGatewayServicesCartAddOnMappingV1[];
}

export interface SpecialistLocationGatewayServicesCartParentReferenceV1 {
  locationId: string;
  userLocationId: string;
  catalogVersion: string;
  serviceId: string;
  offeringId: string;
}

export interface SpecialistLocationGatewayServicesCartSeedLineageV1 {
  kind: "source-a-to-selected-b-services-seed";
  mappingId: string;
  globalRef: string;
  sourceParent: SpecialistLocationGatewayServicesCartParentReferenceV1;
  destinationParent: SpecialistLocationGatewayServicesCartParentReferenceV1;
  sourceConfiguration: SpecialistLocationGatewayServicesCartConfigurationV1 | null;
  variantMapping: SpecialistLocationGatewayServicesCartVariantMappingV1 | null;
  addOnMappings: readonly SpecialistLocationGatewayServicesCartAddOnMappingV1[];
  bundleItemMappings: readonly SpecialistLocationGatewayServicesCartBundleItemMappingV1[];
  mappingFingerprint: string;
}

export interface SpecialistLocationGatewayServicesCartLineV1 {
  lineId: string;
  order: number;
  offeringKind: "service" | "service-bundle";
  destinationServiceId: string;
  destinationOfferingId: string;
  configurationState: "configured" | "configuration-required";
  configuration: SpecialistLocationGatewayServicesCartConfigurationV1 | null;
  /** Only the one scoped Profile seed may carry source-A lineage. */
  seed: SpecialistLocationGatewayServicesCartSeedLineageV1 | null;
}

export interface SpecialistLocationGatewayConfiguredServicesCartV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_SCHEMA_VERSION;
  kind: typeof SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_KIND;
  draftId: string;
  ownerKey: string;
  fingerprint: string;
  customerAccountId: string | null;
  intentDraftId: string;
  resolutionId: string;
  revision: number;
  createdAtEpochMs: number;
  updatedAtEpochMs: number;
  expiresAtEpochMs: number;
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
  lines: readonly SpecialistLocationGatewayServicesCartLineV1[];
}

export type SpecialistLocationGatewayServicesCartValidation =
  | { valid: true; draft: SpecialistLocationGatewayConfiguredServicesCartV1 }
  | { valid: false; reason: string };

export type SpecialistLocationGatewayServicesCartSeedLineageInputV1 = Omit<
  SpecialistLocationGatewayServicesCartSeedLineageV1,
  "mappingFingerprint"
>;

export type SpecialistLocationGatewayServicesCartLineInputV1 = Omit<
  SpecialistLocationGatewayServicesCartLineV1,
  "seed"
> & {
  seed: SpecialistLocationGatewayServicesCartSeedLineageInputV1 | null;
};

export interface CreateSpecialistLocationGatewayConfiguredServicesCartInput {
  draftId: string;
  customerAccountId: string | null;
  intentDraftId: string;
  resolutionId: string;
  revision: number;
  createdAtEpochMs: number;
  updatedAtEpochMs: number;
  expiresAtEpochMs: number;
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
  lines: readonly SpecialistLocationGatewayServicesCartLineInputV1[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
) {
  const keys = Object.keys(value);
  return (
    keys.length === expected.length &&
    keys.every((key) => expected.includes(key))
  );
}

function isEpoch(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isRevision(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isSafeLocalSpecialistHref(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > 2_048 ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    /[\u0000-\u001f\u007f]/u.test(value)
  ) {
    return false;
  }
  try {
    const origin = "https://widget.volera.invalid";
    const parsed = new URL(value, origin);
    return (
      parsed.origin === origin &&
      parsed.username === "" &&
      parsed.password === "" &&
      parsed.hash === "" &&
      `${parsed.pathname}${parsed.search}` === value
    );
  } catch {
    return false;
  }
}

function createInjectiveFingerprint(prefix: string, fields: readonly string[]) {
  return `${prefix}${encodeSpecialistLocationGatewayTuple(fields)}`;
}

function isInjectiveFingerprint(value: unknown, prefix: string) {
  if (
    typeof value !== "string" ||
    !value.startsWith(prefix) ||
    value.length > MAX_CANONICAL_FINGERPRINT_LENGTH
  ) {
    return false;
  }
  const encoded = value.slice(prefix.length);
  try {
    const parsed = JSON.parse(encoded) as unknown;
    return (
      Array.isArray(parsed) &&
      parsed.every((field) => typeof field === "string") &&
      JSON.stringify(parsed) === encoded
    );
  } catch {
    return false;
  }
}

export function isSpecialistLocationGatewayServicesCartFingerprint(
  value: unknown,
) {
  return isInjectiveFingerprint(value, CART_FINGERPRINT_PREFIX);
}

export function isSpecialistLocationGatewayServicesCartMappingFingerprint(
  value: unknown,
) {
  return isInjectiveFingerprint(value, MAPPING_FINGERPRINT_PREFIX);
}

function clonePublicationReference(
  value: unknown,
): SpecialistLocationGatewayServicesPublicationReferenceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "providerId",
      "providerRevision",
      "publicationId",
      "publicationRevision",
      "publicationFingerprint",
      "publishedAtEpochMs",
      "expiresAtEpochMs",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.providerId) ||
    !isSpecialistLocationGatewayIdentifier(value.providerRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.publicationId) ||
    !isSpecialistLocationGatewayIdentifier(value.publicationRevision) ||
    !isSpecialistLocationGatewayFingerprint(value.publicationFingerprint) ||
    !isEpoch(value.publishedAtEpochMs) ||
    !isEpoch(value.expiresAtEpochMs) ||
    value.expiresAtEpochMs <= value.publishedAtEpochMs
  ) {
    return null;
  }
  return {
    providerId: value.providerId,
    providerRevision: value.providerRevision,
    publicationId: value.publicationId,
    publicationRevision: value.publicationRevision,
    publicationFingerprint: value.publicationFingerprint,
    publishedAtEpochMs: value.publishedAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
  };
}

function cloneCatalogReference(
  value: unknown,
): SpecialistLocationGatewayServicesCatalogReferenceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "locationId",
      "catalogOwnerUserLocationId",
      "providerId",
      "providerRevision",
      "publicationId",
      "publicationRevision",
      "catalogVersion",
      "catalogFingerprint",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.catalogOwnerUserLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.providerId) ||
    !isSpecialistLocationGatewayIdentifier(value.providerRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.publicationId) ||
    !isSpecialistLocationGatewayIdentifier(value.publicationRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.catalogVersion) ||
    !isSpecialistLocationGatewayFingerprint(value.catalogFingerprint)
  ) {
    return null;
  }
  return {
    locationId: value.locationId,
    catalogOwnerUserLocationId: value.catalogOwnerUserLocationId,
    providerId: value.providerId,
    providerRevision: value.providerRevision,
    publicationId: value.publicationId,
    publicationRevision: value.publicationRevision,
    catalogVersion: value.catalogVersion,
    catalogFingerprint: value.catalogFingerprint,
  };
}

function cloneSourceAssignment(
  value: unknown,
): SpecialistLocationGatewayServicesSourceAssignmentAV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "personId",
      "locationId",
      "userLocationId",
      "canonicalReturnHref",
      "assignmentProfileRevision",
      "catalog",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.personId) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.userLocationId) ||
    !isSafeLocalSpecialistHref(value.canonicalReturnHref) ||
    !isSpecialistLocationGatewayIdentifier(value.assignmentProfileRevision)
  ) {
    return null;
  }
  const catalog = cloneCatalogReference(value.catalog);
  if (
    !catalog ||
    catalog.locationId !== value.locationId ||
    catalog.catalogOwnerUserLocationId !== value.userLocationId ||
    value.canonicalReturnHref !==
      `/locations/${encodeURIComponent(value.locationId)}/specialists/${encodeURIComponent(
        value.userLocationId,
      )}`
  ) {
    return null;
  }
  return {
    personId: value.personId,
    locationId: value.locationId,
    userLocationId: value.userLocationId,
    canonicalReturnHref: value.canonicalReturnHref,
    assignmentProfileRevision: value.assignmentProfileRevision,
    catalog,
  };
}

function cloneSelectedAssignment(
  value: unknown,
): SpecialistLocationGatewayServicesSelectedAssignmentBV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "personId",
      "locationId",
      "userLocationId",
      "assignmentProfileRevision",
      "gatewayCapabilityRevision",
      "organizerCatalog",
      "locationCatalog",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.personId) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.userLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.assignmentProfileRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayCapabilityRevision)
  ) {
    return null;
  }
  const organizerCatalog = cloneCatalogReference(value.organizerCatalog);
  const locationCatalog = cloneCatalogReference(value.locationCatalog);
  if (
    !organizerCatalog ||
    !locationCatalog ||
    organizerCatalog.locationId !== value.locationId ||
    organizerCatalog.catalogOwnerUserLocationId !== value.userLocationId ||
    locationCatalog.locationId !== value.locationId
  ) {
    return null;
  }
  return {
    personId: value.personId,
    locationId: value.locationId,
    userLocationId: value.userLocationId,
    assignmentProfileRevision: value.assignmentProfileRevision,
    gatewayCapabilityRevision: value.gatewayCapabilityRevision,
    organizerCatalog,
    locationCatalog,
  };
}

function cloneScope(
  value: unknown,
): SpecialistLocationGatewayServicesCartScopeV1 | null {
  if (!isRecord(value)) return null;
  if (value.kind === "unscoped") {
    return Object.keys(value).length === 1 ? { kind: "unscoped" } : null;
  }
  if (
    value.kind !== "scoped" ||
    !hasExactKeys(value, [
      "kind",
      "intentKind",
      "globalRef",
      "sourceServiceId",
      "sourceOfferingId",
      "seedLineId",
    ]) ||
    (value.intentKind !== "service" &&
      value.intentKind !== "service-bundle") ||
    !isSpecialistLocationGatewayIdentifier(value.globalRef) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceServiceId) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceOfferingId) ||
    !isSpecialistLocationGatewayIdentifier(value.seedLineId)
  ) {
    return null;
  }
  return {
    kind: "scoped",
    intentKind: value.intentKind,
    globalRef: value.globalRef,
    sourceServiceId: value.sourceServiceId,
    sourceOfferingId: value.sourceOfferingId,
    seedLineId: value.seedLineId,
  };
}

function cloneAddOns(
  value: unknown,
): readonly SpecialistLocationGatewayServicesCartAddOnSelectionV1[] | null {
  if (!Array.isArray(value) || value.length > MAX_ADD_ON_ROWS) return null;
  const seen = new Set<string>();
  const rows: SpecialistLocationGatewayServicesCartAddOnSelectionV1[] = [];
  for (const row of value) {
    if (
      !isRecord(row) ||
      !hasExactKeys(row, ["groupId", "addOnId", "quantity"]) ||
      !isSpecialistLocationGatewayIdentifier(row.groupId) ||
      !isSpecialistLocationGatewayIdentifier(row.addOnId) ||
      !Number.isSafeInteger(row.quantity) ||
      Number(row.quantity) < 1 ||
      Number(row.quantity) > MAX_QUANTITY
    ) {
      return null;
    }
    const identity = encodeSpecialistLocationGatewayTuple([
      row.groupId,
      row.addOnId,
    ]);
    if (seen.has(identity)) return null;
    seen.add(identity);
    rows.push({
      groupId: row.groupId,
      addOnId: row.addOnId,
      quantity: row.quantity as number,
    });
  }
  return rows;
}

function cloneConfiguration(
  value: unknown,
  offeringKind: "service" | "service-bundle",
): SpecialistLocationGatewayServicesCartConfigurationV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["variantId", "addOns", "bundleItems"]) ||
    (value.variantId !== null &&
      !isSpecialistLocationGatewayIdentifier(value.variantId)) ||
    !Array.isArray(value.bundleItems) ||
    value.bundleItems.length > MAX_BUNDLE_ITEM_ROWS
  ) {
    return null;
  }
  const addOns = cloneAddOns(value.addOns);
  if (!addOns) return null;
  const bundleItems: SpecialistLocationGatewayServicesCartBundleItemSelectionV1[] = [];
  const seenBundleItems = new Set<string>();
  for (const item of value.bundleItems) {
    if (
      !isRecord(item) ||
      !hasExactKeys(item, [
        "bundleItemId",
        "serviceId",
        "offeringId",
        "variantId",
        "addOns",
      ]) ||
      !isSpecialistLocationGatewayIdentifier(item.bundleItemId) ||
      !isSpecialistLocationGatewayIdentifier(item.serviceId) ||
      !isSpecialistLocationGatewayIdentifier(item.offeringId) ||
      (item.variantId !== null &&
        !isSpecialistLocationGatewayIdentifier(item.variantId))
    ) {
      return null;
    }
    const itemAddOns = cloneAddOns(item.addOns);
    if (!itemAddOns || seenBundleItems.has(item.bundleItemId)) return null;
    seenBundleItems.add(item.bundleItemId);
    bundleItems.push({
      bundleItemId: item.bundleItemId,
      serviceId: item.serviceId,
      offeringId: item.offeringId,
      variantId: item.variantId as string | null,
      addOns: itemAddOns,
    });
  }
  if (
    (offeringKind === "service" && bundleItems.length > 0) ||
    (offeringKind === "service-bundle" &&
      (value.variantId !== null || addOns.length > 0 || bundleItems.length === 0))
  ) {
    return null;
  }
  return {
    variantId: value.variantId as string | null,
    addOns,
    bundleItems,
  };
}

function cloneVariantMapping(
  value: unknown,
): SpecialistLocationGatewayServicesCartVariantMappingV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["sourceVariantId", "destinationVariantId"]) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceVariantId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationVariantId)
  ) {
    return null;
  }
  return {
    sourceVariantId: value.sourceVariantId,
    destinationVariantId: value.destinationVariantId,
  };
}

function cloneAddOnMappings(
  value: unknown,
): readonly SpecialistLocationGatewayServicesCartAddOnMappingV1[] | null {
  if (!Array.isArray(value) || value.length > MAX_ADD_ON_ROWS) return null;
  const mappings: SpecialistLocationGatewayServicesCartAddOnMappingV1[] = [];
  const sourceSeen = new Set<string>();
  const destinationSeen = new Set<string>();
  for (const [order, row] of value.entries()) {
    if (
      !isRecord(row) ||
      !hasExactKeys(row, [
        "order",
        "sourceGroupId",
        "sourceAddOnId",
        "destinationGroupId",
        "destinationAddOnId",
        "quantity",
      ]) ||
      row.order !== order ||
      !isSpecialistLocationGatewayIdentifier(row.sourceGroupId) ||
      !isSpecialistLocationGatewayIdentifier(row.sourceAddOnId) ||
      !isSpecialistLocationGatewayIdentifier(row.destinationGroupId) ||
      !isSpecialistLocationGatewayIdentifier(row.destinationAddOnId) ||
      !Number.isSafeInteger(row.quantity) ||
      Number(row.quantity) < 1 ||
      Number(row.quantity) > MAX_QUANTITY
    ) {
      return null;
    }
    const sourceKey = encodeSpecialistLocationGatewayTuple([
      row.sourceGroupId,
      row.sourceAddOnId,
    ]);
    const destinationKey = encodeSpecialistLocationGatewayTuple([
      row.destinationGroupId,
      row.destinationAddOnId,
    ]);
    if (sourceSeen.has(sourceKey) || destinationSeen.has(destinationKey)) {
      return null;
    }
    sourceSeen.add(sourceKey);
    destinationSeen.add(destinationKey);
    mappings.push({
      order,
      sourceGroupId: row.sourceGroupId,
      sourceAddOnId: row.sourceAddOnId,
      destinationGroupId: row.destinationGroupId,
      destinationAddOnId: row.destinationAddOnId,
      quantity: row.quantity as number,
    });
  }
  return mappings;
}

function variantMappingIsExact(
  sourceVariantId: string | null,
  destinationVariantId: string | null,
  mapping: SpecialistLocationGatewayServicesCartVariantMappingV1 | null,
) {
  if (sourceVariantId === null || destinationVariantId === null) {
    return sourceVariantId === null && destinationVariantId === null && !mapping;
  }
  return (
    mapping?.sourceVariantId === sourceVariantId &&
    mapping.destinationVariantId === destinationVariantId
  );
}

function addOnMappingsAreExact(
  source: readonly SpecialistLocationGatewayServicesCartAddOnSelectionV1[],
  destination: readonly SpecialistLocationGatewayServicesCartAddOnSelectionV1[],
  mappings: readonly SpecialistLocationGatewayServicesCartAddOnMappingV1[],
) {
  return (
    source.length === destination.length &&
    mappings.length === source.length &&
    mappings.every((mapping, order) => {
      const sourceRow = source[order];
      const destinationRow = destination[order];
      return (
        sourceRow &&
        destinationRow &&
        mapping.order === order &&
        mapping.sourceGroupId === sourceRow.groupId &&
        mapping.sourceAddOnId === sourceRow.addOnId &&
        mapping.destinationGroupId === destinationRow.groupId &&
        mapping.destinationAddOnId === destinationRow.addOnId &&
        mapping.quantity === sourceRow.quantity &&
        mapping.quantity === destinationRow.quantity
      );
    })
  );
}

function cloneParentReference(
  value: unknown,
): SpecialistLocationGatewayServicesCartParentReferenceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "locationId",
      "userLocationId",
      "catalogVersion",
      "serviceId",
      "offeringId",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.userLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.catalogVersion) ||
    !isSpecialistLocationGatewayIdentifier(value.serviceId) ||
    !isSpecialistLocationGatewayIdentifier(value.offeringId)
  ) {
    return null;
  }
  return {
    locationId: value.locationId,
    userLocationId: value.userLocationId,
    catalogVersion: value.catalogVersion,
    serviceId: value.serviceId,
    offeringId: value.offeringId,
  };
}

function cloneBundleItemMappings(input: {
  value: unknown;
  source: readonly SpecialistLocationGatewayServicesCartBundleItemSelectionV1[];
  destination: readonly SpecialistLocationGatewayServicesCartBundleItemSelectionV1[];
}): readonly SpecialistLocationGatewayServicesCartBundleItemMappingV1[] | null {
  if (
    !Array.isArray(input.value) ||
    input.value.length !== input.source.length ||
    input.value.length !== input.destination.length
  ) {
    return null;
  }
  const mappings: SpecialistLocationGatewayServicesCartBundleItemMappingV1[] = [];
  const sourceSeen = new Set<string>();
  const destinationSeen = new Set<string>();
  for (const [order, raw] of input.value.entries()) {
    if (
      !isRecord(raw) ||
      !hasExactKeys(raw, [
        "order",
        "sourceBundleItemId",
        "sourceServiceId",
        "sourceOfferingId",
        "destinationBundleItemId",
        "destinationServiceId",
        "destinationOfferingId",
        "variantMapping",
        "addOnMappings",
      ]) ||
      raw.order !== order ||
      !isSpecialistLocationGatewayIdentifier(raw.sourceBundleItemId) ||
      !isSpecialistLocationGatewayIdentifier(raw.sourceServiceId) ||
      !isSpecialistLocationGatewayIdentifier(raw.sourceOfferingId) ||
      !isSpecialistLocationGatewayIdentifier(raw.destinationBundleItemId) ||
      !isSpecialistLocationGatewayIdentifier(raw.destinationServiceId) ||
      !isSpecialistLocationGatewayIdentifier(raw.destinationOfferingId)
    ) {
      return null;
    }
    const source = input.source[order];
    const destination = input.destination[order];
    const variantMapping =
      raw.variantMapping === null
        ? null
        : cloneVariantMapping(raw.variantMapping);
    const addOnMappings = cloneAddOnMappings(raw.addOnMappings);
    if (
      !source ||
      !destination ||
      (raw.variantMapping !== null && !variantMapping) ||
      !addOnMappings ||
      raw.sourceBundleItemId !== source.bundleItemId ||
      raw.sourceServiceId !== source.serviceId ||
      raw.sourceOfferingId !== source.offeringId ||
      raw.destinationBundleItemId !== destination.bundleItemId ||
      raw.destinationServiceId !== destination.serviceId ||
      raw.destinationOfferingId !== destination.offeringId ||
      !variantMappingIsExact(
        source.variantId,
        destination.variantId,
        variantMapping,
      ) ||
      !addOnMappingsAreExact(source.addOns, destination.addOns, addOnMappings) ||
      sourceSeen.has(source.bundleItemId) ||
      destinationSeen.has(destination.bundleItemId)
    ) {
      return null;
    }
    sourceSeen.add(source.bundleItemId);
    destinationSeen.add(destination.bundleItemId);
    mappings.push({
      order,
      sourceBundleItemId: raw.sourceBundleItemId,
      sourceServiceId: raw.sourceServiceId,
      sourceOfferingId: raw.sourceOfferingId,
      destinationBundleItemId: raw.destinationBundleItemId,
      destinationServiceId: raw.destinationServiceId,
      destinationOfferingId: raw.destinationOfferingId,
      variantMapping,
      addOnMappings,
    });
  }
  return mappings;
}

function mappingFingerprintBody(input: {
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
  lineId: string;
  offeringKind: "service" | "service-bundle";
  destinationServiceId: string;
  destinationOfferingId: string;
  configurationState: "configured" | "configuration-required";
  configuration: SpecialistLocationGatewayServicesCartConfigurationV1 | null;
  seed: SpecialistLocationGatewayServicesCartSeedLineageInputV1;
}) {
  return JSON.stringify({
    gatewayPublication: input.gatewayPublication,
    mappingPublication: input.mappingPublication,
    sourceAssignmentA: input.sourceAssignmentA,
    selectedAssignmentB: input.selectedAssignmentB,
    scope: input.scope,
    lineId: input.lineId,
    offeringKind: input.offeringKind,
    destinationServiceId: input.destinationServiceId,
    destinationOfferingId: input.destinationOfferingId,
    configurationState: input.configurationState,
    configuration: input.configuration,
    seed: input.seed,
  });
}

export function createSpecialistLocationGatewayServicesCartMappingFingerprint(
  input: Parameters<typeof mappingFingerprintBody>[0],
) {
  return createInjectiveFingerprint(MAPPING_FINGERPRINT_PREFIX, [
    "source-a-to-selected-b-services-seed-v1",
    mappingFingerprintBody(input),
  ]);
}

function cloneSeed(input: {
  value: unknown;
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
  lineId: string;
  offeringKind: "service" | "service-bundle";
  destinationServiceId: string;
  destinationOfferingId: string;
  configurationState: "configured" | "configuration-required";
  configuration: SpecialistLocationGatewayServicesCartConfigurationV1 | null;
}): SpecialistLocationGatewayServicesCartSeedLineageV1 | null {
  const { value } = input;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "mappingId",
      "globalRef",
      "sourceParent",
      "destinationParent",
      "sourceConfiguration",
      "variantMapping",
      "addOnMappings",
      "bundleItemMappings",
      "mappingFingerprint",
    ]) ||
    value.kind !== "source-a-to-selected-b-services-seed" ||
    !isSpecialistLocationGatewayIdentifier(value.mappingId) ||
    !isSpecialistLocationGatewayIdentifier(value.globalRef) ||
    !isSpecialistLocationGatewayServicesCartMappingFingerprint(
      value.mappingFingerprint,
    )
  ) {
    return null;
  }
  const sourceParent = cloneParentReference(value.sourceParent);
  const destinationParent = cloneParentReference(value.destinationParent);
  if (!sourceParent || !destinationParent || input.scope.kind !== "scoped") {
    return null;
  }
  const sourceConfiguration =
    value.sourceConfiguration === null
      ? null
      : cloneConfiguration(value.sourceConfiguration, input.offeringKind);
  const variantMapping =
    value.variantMapping === null
      ? null
      : cloneVariantMapping(value.variantMapping);
  const addOnMappings = cloneAddOnMappings(value.addOnMappings);
  if (
    (value.sourceConfiguration !== null && !sourceConfiguration) ||
    (value.variantMapping !== null && !variantMapping) ||
    !addOnMappings ||
    sourceParent.locationId !== input.sourceAssignmentA.locationId ||
    sourceParent.userLocationId !== input.sourceAssignmentA.userLocationId ||
    sourceParent.catalogVersion !== input.sourceAssignmentA.catalog.catalogVersion ||
    sourceParent.serviceId !== input.scope.sourceServiceId ||
    sourceParent.offeringId !== input.scope.sourceOfferingId ||
    destinationParent.locationId !== input.selectedAssignmentB.locationId ||
    destinationParent.userLocationId !== input.selectedAssignmentB.userLocationId ||
    destinationParent.catalogVersion !==
      input.selectedAssignmentB.organizerCatalog.catalogVersion ||
    destinationParent.serviceId !== input.destinationServiceId ||
    destinationParent.offeringId !== input.destinationOfferingId ||
    value.globalRef !== input.scope.globalRef ||
    input.scope.seedLineId !== input.lineId ||
    input.scope.intentKind !== input.offeringKind
  ) {
    return null;
  }
  let bundleItemMappings: readonly SpecialistLocationGatewayServicesCartBundleItemMappingV1[];
  if (input.configurationState === "configuration-required") {
    if (
      input.configuration !== null ||
      sourceConfiguration !== null ||
      variantMapping !== null ||
      addOnMappings.length !== 0 ||
      !Array.isArray(value.bundleItemMappings) ||
      value.bundleItemMappings.length !== 0
    ) {
      return null;
    }
    bundleItemMappings = [];
  } else {
    if (!sourceConfiguration || !input.configuration) return null;
    if (
      !variantMappingIsExact(
        sourceConfiguration.variantId,
        input.configuration.variantId,
        variantMapping,
      ) ||
      !addOnMappingsAreExact(
        sourceConfiguration.addOns,
        input.configuration.addOns,
        addOnMappings,
      )
    ) {
      return null;
    }
    const clonedBundleMappings = cloneBundleItemMappings({
      value: value.bundleItemMappings,
      source: sourceConfiguration.bundleItems,
      destination: input.configuration.bundleItems,
    });
    if (!clonedBundleMappings) return null;
    bundleItemMappings = clonedBundleMappings;
  }
  const seedWithoutFingerprint: SpecialistLocationGatewayServicesCartSeedLineageInputV1 = {
    kind: "source-a-to-selected-b-services-seed",
    mappingId: value.mappingId,
    globalRef: value.globalRef,
    sourceParent,
    destinationParent,
    sourceConfiguration,
    variantMapping,
    addOnMappings,
    bundleItemMappings,
  };
  const expectedFingerprint =
    createSpecialistLocationGatewayServicesCartMappingFingerprint({
      ...input,
      seed: seedWithoutFingerprint,
    });
  return value.mappingFingerprint === expectedFingerprint
    ? { ...seedWithoutFingerprint, mappingFingerprint: expectedFingerprint }
    : null;
}

function cloneLines(input: {
  value: unknown;
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
}): readonly SpecialistLocationGatewayServicesCartLineV1[] | null {
  if (
    !Array.isArray(input.value) ||
    input.value.length === 0 ||
    input.value.length > SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_LINES
  ) {
    return null;
  }
  const lineIds = new Set<string>();
  const destinationParents = new Set<string>();
  const mappingIds = new Set<string>();
  const lines: SpecialistLocationGatewayServicesCartLineV1[] = [];
  for (const [order, value] of input.value.entries()) {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "lineId",
        "order",
        "offeringKind",
        "destinationServiceId",
        "destinationOfferingId",
        "configurationState",
        "configuration",
        "seed",
      ]) ||
      !isSpecialistLocationGatewayIdentifier(value.lineId) ||
      value.order !== order ||
      (value.offeringKind !== "service" &&
        value.offeringKind !== "service-bundle") ||
      !isSpecialistLocationGatewayIdentifier(value.destinationServiceId) ||
      !isSpecialistLocationGatewayIdentifier(value.destinationOfferingId) ||
      (value.configurationState !== "configured" &&
        value.configurationState !== "configuration-required")
    ) {
      return null;
    }
    const configuration =
      value.configuration === null
        ? null
        : cloneConfiguration(value.configuration, value.offeringKind);
    if (
      (value.configurationState === "configured" && !configuration) ||
      (value.configurationState === "configuration-required" &&
        value.configuration !== null)
    ) {
      return null;
    }
    const parentKey = encodeSpecialistLocationGatewayTuple([
      value.destinationServiceId,
      value.destinationOfferingId,
    ]);
    if (lineIds.has(value.lineId) || destinationParents.has(parentKey)) {
      return null;
    }
    lineIds.add(value.lineId);
    destinationParents.add(parentKey);
    const seed =
      value.seed === null
        ? null
        : cloneSeed({
            ...input,
            value: value.seed,
            lineId: value.lineId,
            offeringKind: value.offeringKind,
            destinationServiceId: value.destinationServiceId,
            destinationOfferingId: value.destinationOfferingId,
            configurationState: value.configurationState,
            configuration,
          });
    if (value.seed !== null && !seed) return null;
    if (seed) {
      if (mappingIds.has(seed.mappingId)) return null;
      mappingIds.add(seed.mappingId);
    }
    lines.push({
      lineId: value.lineId,
      order,
      offeringKind: value.offeringKind,
      destinationServiceId: value.destinationServiceId,
      destinationOfferingId: value.destinationOfferingId,
      configurationState: value.configurationState,
      configuration,
      seed,
    });
  }
  const seededLines = lines.filter(({ seed }) => seed !== null);
  if (
    (input.scope.kind === "unscoped" && seededLines.length !== 0) ||
    (input.scope.kind === "scoped" &&
      (seededLines.length !== 1 ||
        seededLines[0]?.lineId !== input.scope.seedLineId))
  ) {
    return null;
  }
  return lines;
}

function ownerKeyFor(input: {
  draftId: string;
  customerAccountId: string | null;
  intentDraftId: string;
  resolutionId: string;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  gatewayPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  mappingPublication: SpecialistLocationGatewayServicesPublicationReferenceV1;
  sourceAssignmentA: SpecialistLocationGatewayServicesSourceAssignmentAV1;
  selectedAssignmentB: SpecialistLocationGatewayServicesSelectedAssignmentBV1;
  scope: SpecialistLocationGatewayServicesCartScopeV1;
}) {
  return createSpecialistLocationGatewayOwnerKey({
    stage: "configured-services-cart-v1",
    fields: [
      input.draftId,
      input.customerAccountId,
      input.intentDraftId,
      input.resolutionId,
      input.createdAtEpochMs,
      input.expiresAtEpochMs,
      JSON.stringify(input.gatewayPublication),
      JSON.stringify(input.mappingPublication),
      JSON.stringify(input.sourceAssignmentA),
      JSON.stringify(input.selectedAssignmentB),
      JSON.stringify(input.scope),
    ],
  });
}

function publicationFingerprintBody(
  value: Omit<SpecialistLocationGatewayConfiguredServicesCartV1, "fingerprint">,
) {
  return JSON.stringify(value);
}

function publicationFingerprint(
  value: Omit<SpecialistLocationGatewayConfiguredServicesCartV1, "fingerprint">,
) {
  return createInjectiveFingerprint(CART_FINGERPRINT_PREFIX, [
    "configured-services-cart-v1",
    publicationFingerprintBody(value),
  ]);
}

export function createSpecialistLocationGatewayConfiguredServicesCart(
  input: CreateSpecialistLocationGatewayConfiguredServicesCartInput,
): SpecialistLocationGatewayConfiguredServicesCartV1 | null {
  try {
    const lines = input.lines.map<SpecialistLocationGatewayServicesCartLineV1>(
      (line) => ({
        ...line,
        seed: line.seed
          ? {
              ...line.seed,
              mappingFingerprint:
                createSpecialistLocationGatewayServicesCartMappingFingerprint({
                  gatewayPublication: input.gatewayPublication,
                  mappingPublication: input.mappingPublication,
                  sourceAssignmentA: input.sourceAssignmentA,
                  selectedAssignmentB: input.selectedAssignmentB,
                  scope: input.scope,
                  lineId: line.lineId,
                  offeringKind: line.offeringKind,
                  destinationServiceId: line.destinationServiceId,
                  destinationOfferingId: line.destinationOfferingId,
                  configurationState: line.configurationState,
                  configuration: line.configuration,
                  seed: line.seed,
                }),
            }
          : null,
      }),
    );
    const ownerKey = ownerKeyFor(input);
    const body: Omit<
      SpecialistLocationGatewayConfiguredServicesCartV1,
      "fingerprint"
    > = {
      schemaVersion: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_SCHEMA_VERSION,
      kind: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_KIND,
      draftId: input.draftId,
      ownerKey,
      customerAccountId: input.customerAccountId,
      intentDraftId: input.intentDraftId,
      resolutionId: input.resolutionId,
      revision: input.revision,
      createdAtEpochMs: input.createdAtEpochMs,
      updatedAtEpochMs: input.updatedAtEpochMs,
      expiresAtEpochMs: input.expiresAtEpochMs,
      gatewayPublication: input.gatewayPublication,
      mappingPublication: input.mappingPublication,
      sourceAssignmentA: input.sourceAssignmentA,
      selectedAssignmentB: input.selectedAssignmentB,
      scope: input.scope,
      lines,
    };
    const candidate = { ...body, fingerprint: publicationFingerprint(body) };
    const validation = validateSpecialistLocationGatewayConfiguredServicesCart(
      candidate,
    );
    return validation.valid ? validation.draft : null;
  } catch {
    return null;
  }
}

export function validateSpecialistLocationGatewayConfiguredServicesCart(
  value: unknown,
): SpecialistLocationGatewayServicesCartValidation {
  const invalid = (reason: string): SpecialistLocationGatewayServicesCartValidation => ({
    valid: false,
    reason,
  });
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "draftId",
      "ownerKey",
      "fingerprint",
      "customerAccountId",
      "intentDraftId",
      "resolutionId",
      "revision",
      "createdAtEpochMs",
      "updatedAtEpochMs",
      "expiresAtEpochMs",
      "gatewayPublication",
      "mappingPublication",
      "sourceAssignmentA",
      "selectedAssignmentB",
      "scope",
      "lines",
    ]) ||
    value.schemaVersion !==
      SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_SCHEMA_VERSION ||
    value.kind !== SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_KIND ||
    !isSpecialistLocationGatewayIdentifier(value.draftId) ||
    typeof value.ownerKey !== "string" ||
    value.ownerKey.length === 0 ||
    value.ownerKey.length > 120_000 ||
    !isSpecialistLocationGatewayServicesCartFingerprint(value.fingerprint) ||
    (value.customerAccountId !== null &&
      !isSpecialistLocationGatewayIdentifier(value.customerAccountId)) ||
    !isSpecialistLocationGatewayIdentifier(value.intentDraftId) ||
    !isSpecialistLocationGatewayIdentifier(value.resolutionId) ||
    !isRevision(value.revision) ||
    !isEpoch(value.createdAtEpochMs) ||
    !isEpoch(value.updatedAtEpochMs) ||
    !isEpoch(value.expiresAtEpochMs) ||
    value.updatedAtEpochMs < value.createdAtEpochMs ||
    value.updatedAtEpochMs >= value.expiresAtEpochMs ||
    value.expiresAtEpochMs - value.createdAtEpochMs >
      SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_TTL_MS
  ) {
    return invalid("invalid-specialist-location-gateway-services-cart");
  }
  const gatewayPublication = clonePublicationReference(value.gatewayPublication);
  const mappingPublication = clonePublicationReference(value.mappingPublication);
  const sourceAssignmentA = cloneSourceAssignment(value.sourceAssignmentA);
  const selectedAssignmentB = cloneSelectedAssignment(value.selectedAssignmentB);
  const scope = cloneScope(value.scope);
  if (
    !gatewayPublication ||
    !mappingPublication ||
    !sourceAssignmentA ||
    !selectedAssignmentB ||
    !scope ||
    sourceAssignmentA.personId !== selectedAssignmentB.personId ||
    gatewayPublication.publishedAtEpochMs >
      value.createdAtEpochMs + MAX_CLOCK_SKEW_MS ||
    mappingPublication.publishedAtEpochMs >
      value.createdAtEpochMs + MAX_CLOCK_SKEW_MS ||
    gatewayPublication.expiresAtEpochMs < value.expiresAtEpochMs ||
    mappingPublication.expiresAtEpochMs < value.expiresAtEpochMs
  ) {
    return invalid("specialist-location-gateway-services-cart-authority-mismatch");
  }
  const lines = cloneLines({
    value: value.lines,
    gatewayPublication,
    mappingPublication,
    sourceAssignmentA,
    selectedAssignmentB,
    scope,
  });
  if (!lines) {
    return invalid("specialist-location-gateway-services-cart-lines-invalid");
  }
  const ownerInput = {
    draftId: value.draftId,
    customerAccountId: value.customerAccountId as string | null,
    intentDraftId: value.intentDraftId,
    resolutionId: value.resolutionId,
    createdAtEpochMs: value.createdAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
    gatewayPublication,
    mappingPublication,
    sourceAssignmentA,
    selectedAssignmentB,
    scope,
  };
  const ownerKey = ownerKeyFor(ownerInput);
  if (value.ownerKey !== ownerKey) {
    return invalid("specialist-location-gateway-services-cart-owner-mismatch");
  }
  const body: Omit<
    SpecialistLocationGatewayConfiguredServicesCartV1,
    "fingerprint"
  > = {
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_SCHEMA_VERSION,
    kind: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_KIND,
    draftId: value.draftId,
    ownerKey,
    customerAccountId: value.customerAccountId as string | null,
    intentDraftId: value.intentDraftId,
    resolutionId: value.resolutionId,
    revision: value.revision,
    createdAtEpochMs: value.createdAtEpochMs,
    updatedAtEpochMs: value.updatedAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
    gatewayPublication,
    mappingPublication,
    sourceAssignmentA,
    selectedAssignmentB,
    scope,
    lines,
  };
  const fingerprint = publicationFingerprint(body);
  if (value.fingerprint !== fingerprint) {
    return invalid("specialist-location-gateway-services-cart-fingerprint-mismatch");
  }
  return { valid: true, draft: { ...body, fingerprint } };
}

export function isSpecialistLocationGatewayConfiguredServicesCartFresh(
  value: Pick<
    SpecialistLocationGatewayConfiguredServicesCartV1,
    "createdAtEpochMs" | "expiresAtEpochMs" | "updatedAtEpochMs"
  >,
  nowEpochMs: number,
) {
  return (
    Number.isSafeInteger(nowEpochMs) &&
    nowEpochMs >= 0 &&
    value.createdAtEpochMs <= nowEpochMs + MAX_CLOCK_SKEW_MS &&
    value.updatedAtEpochMs <= nowEpochMs + MAX_CLOCK_SKEW_MS &&
    value.expiresAtEpochMs > nowEpochMs
  );
}

/** Complete configurations alone may cross the eventual Continue boundary. */
export function specialistLocationGatewayServicesCartIsContinuable(
  value: SpecialistLocationGatewayConfiguredServicesCartV1,
) {
  return value.lines.every(
    ({ configurationState, configuration }) =>
      configurationState === "configured" && configuration !== null,
  );
}

/**
 * CAS replacements may edit/reorder cart rows, but never rewrite the frozen
 * A/B/publication owner or substitute a different scoped seed identity.
 * A retained `lineId` is bound to the exact
 * `{offeringKind, destinationServiceId, destinationOfferingId}` tuple. V1
 * keeps no historical tombstone after a line disappears, so the provider that
 * mints IDs must never recycle a removed ID for another parent; durable
 * cross-revision reuse would require a schema bump with an identity ledger.
 */
export function isSpecialistLocationGatewayServicesCartRevisionSuccessor(
  expected: SpecialistLocationGatewayConfiguredServicesCartV1,
  next: SpecialistLocationGatewayConfiguredServicesCartV1,
) {
  const expectedByParent = new Map(
    expected.lines.map((line) => [
      encodeSpecialistLocationGatewayTuple([
        line.destinationServiceId,
        line.destinationOfferingId,
      ]),
      line,
    ]),
  );
  const expectedByLineId = new Map(
    expected.lines.map((line) => [line.lineId, line]),
  );
  const retainedLineIdentitiesAreStable = next.lines.every((line) => {
    const parentKey = encodeSpecialistLocationGatewayTuple([
      line.destinationServiceId,
      line.destinationOfferingId,
    ]);
    const priorParent = expectedByParent.get(parentKey);
    const priorLineId = expectedByLineId.get(line.lineId);
    return (
      (!priorParent || priorParent.lineId === line.lineId) &&
      (!priorLineId ||
        (priorLineId.offeringKind === line.offeringKind &&
          priorLineId.destinationServiceId === line.destinationServiceId &&
          priorLineId.destinationOfferingId === line.destinationOfferingId))
    );
  });
  return (
    expected.draftId === next.draftId &&
    expected.ownerKey === next.ownerKey &&
    expected.customerAccountId === next.customerAccountId &&
    expected.intentDraftId === next.intentDraftId &&
    expected.resolutionId === next.resolutionId &&
    expected.createdAtEpochMs === next.createdAtEpochMs &&
    expected.expiresAtEpochMs === next.expiresAtEpochMs &&
    next.revision === expected.revision + 1 &&
    next.updatedAtEpochMs >= expected.updatedAtEpochMs &&
    retainedLineIdentitiesAreStable &&
    (expected.scope.kind !== "scoped" ||
      (next.scope.kind === "scoped" &&
        expected.scope.seedLineId === next.scope.seedLineId))
  );
}
